import { useEffect } from 'react'

import type {
  EventBinding,
  EventBindingHandler,
  PointerLikeEvent,
  ShouldHandleEscapeParams,
  ShouldHandleOutsideClickFn,
  UseOutsideClickFn,
  UseOutsideClickParams,
} from './useOutsideClick.types'

const OUTSIDE_CLICK_EVENTS = ['mousedown', 'touchstart'] as const
const ESCAPE_KEY = 'Escape'
const ESCAPE_EVENT = 'keydown' as const

/**
 * @function buildEventBindings
 * @description собирает привязки событий (клик вне + Escape)
 * @param params - параметры хука
 * @returns привязки событий
 */
const buildEventBindings = (params: UseOutsideClickParams<HTMLElement>): EventBinding[] => {
  const { ref, handler, onEscape } = params

  const shouldHandleOutsideClick: ShouldHandleOutsideClickFn = (params) => {
    const { ref, event } = params
    const el = ref.current
    const target = event.target
    const targetIsNode = target instanceof Node
    const isInside = el && targetIsNode && el.contains(target)
    return Boolean(el) && !isInside
  }

  const shouldHandleEscape = (params: ShouldHandleEscapeParams): boolean => {
    return params.event.key === ESCAPE_KEY && Boolean(params.onEscape)
  }

  const listener = (event: PointerLikeEvent) => shouldHandleOutsideClick({ ref, event }) && handler(event)
  const handleEscape = (event: KeyboardEvent) => shouldHandleEscape({ event, onEscape }) && onEscape?.()

  const outsideClickBindings = OUTSIDE_CLICK_EVENTS.map((ev) => ({ ev, fn: listener }))
  const escapeBindings = onEscape ? [{ ev: ESCAPE_EVENT, fn: handleEscape as EventBindingHandler, capture: true }] : []

  return [...outsideClickBindings, ...escapeBindings]
}

/**
 * @function useOutsideClick - хук для закрытия по клику вне элемента и Escape
 * использует mousedown и touchstart для лучшей отзывчивости на touch-устройствах
 */
const useOutsideClick: UseOutsideClickFn = (params) => {
  const { ref, handler, onEscape } = params

  useEffect(() => {
    const bindings = buildEventBindings({ ref, handler, onEscape })
    bindings.forEach(({ ev, fn, capture }) => document.addEventListener(ev, fn, capture))
    return () => bindings.forEach(({ ev, fn, capture }) => document.removeEventListener(ev, fn, capture))
  }, [ref, handler, onEscape])
}

export default useOutsideClick
